'use client';

import * as React from 'react';
import { useFirestore, useAuth } from '@/firebase';
import { Loader2, Plane } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { MediaItem } from '@/models/media-item';
import { HybridMediaItem } from '@/lib/gallery-hybrid';
import { AircraftModelCombobox } from './aircraft-model-combobox';
import { incrementModelUsageCount } from '@/lib/aircraft-models';

interface QuickTagAircraftDialogProps {
  open: boolean;
  onClose: () => void;
  media: MediaItem | HybridMediaItem;
  onUpdate: (mediaId: string, updates: Record<string, any>) => void;
}

export function QuickTagAircraftDialog({
  open,
  onClose,
  media,
  onUpdate,
}: QuickTagAircraftDialogProps) {
  const firestore = useFirestore();
  const auth = useAuth();
  const { toast } = useToast();

  const [aircraftModelIds, setAircraftModelIds] = React.useState<string[]>([]);
  const [aircraftModelNames, setAircraftModelNames] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState(false);

  // Cast to MediaItem to access aircraft fields (HybridMediaItem extends the same base)
  const mediaWithAircraft = media as MediaItem;

  // Initialize from media when dialog opens
  React.useEffect(() => {
    if (open) {
      setAircraftModelIds(mediaWithAircraft.aircraftModelIds || []);
      setAircraftModelNames(mediaWithAircraft.aircraftModelNames || []);
    }
  }, [open, mediaWithAircraft]);

  const handleSave = async () => {
    if (!firestore) return;

    setSaving(true);
    try {
      // Calculate which models were added/removed for usage count updates
      const previousIds = new Set(mediaWithAircraft.aircraftModelIds || []);
      const newIds = new Set(aircraftModelIds);

      const addedIds = aircraftModelIds.filter((id: string) => !previousIds.has(id));
      const removedIds = (mediaWithAircraft.aircraftModelIds || []).filter((id: string) => !newIds.has(id));

      // Update via server API (works even during client quota exhaustion)
      const updates = { aircraftModelIds, aircraftModelNames };
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch('/api/gallery/update-media', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ mediaId: media.id, updates }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || 'Failed to tag aircraft');
      }

      // Update usage counts (fire-and-forget, non-critical)
      Promise.all([
        ...addedIds.map((id: string) => incrementModelUsageCount(firestore, id, 1)),
        ...removedIds.map((id: string) => incrementModelUsageCount(firestore, id, -1)),
      ]).catch(err => console.error('Failed to update usage counts:', err));

      toast({
        title: 'Aircraft Tagged',
        description: aircraftModelIds.length > 0
          ? `Tagged with ${aircraftModelIds.length} aircraft model(s)`
          : 'Aircraft tags removed',
      });

      onUpdate(media.id, updates);
      onClose();
    } catch (error) {
      console.error('Error tagging aircraft:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to tag aircraft',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5" />
            Tag Aircraft
          </DialogTitle>
          <DialogDescription>
            Tag this {media.type} with the aircraft model(s) shown.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <AircraftModelCombobox
            value={aircraftModelIds}
            valueNames={aircraftModelNames}
            onChange={(ids, names) => {
              setAircraftModelIds(ids);
              setAircraftModelNames(names);
            }}
            disabled={saving}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
